import { useState } from 'react';
import { Button } from './Button';
import { formatInstant } from './time';

export interface LogEntry {
  id: number;
  at: number;
  level: 'info' | 'warn' | 'error';
  source: string;
  message: string;
  data?: unknown;
}

/** The Logs tab of the dock (slice 6 feeds it with the extensions' log): level filters with counts, Clear per source or all. */
export function LogPanel({ entries, onClear }: { entries: LogEntry[]; onClear: (source?: string) => void }) {
  const [level, setLevel] = useState<'all' | LogEntry['level']>('all');
  const counts = { info: entries.filter((e) => e.level === 'info').length, warn: entries.filter((e) => e.level === 'warn').length, error: entries.filter((e) => e.level === 'error').length };
  const shown = level === 'all' ? entries : entries.filter((e) => e.level === level);
  const sources = Array.from(new Set(entries.map((e) => e.source)));
  return (
    <div aria-label="Log">
      <div className="row wrap" style={{ marginBottom: 8 }}>
        {(['all', 'info', 'warn', 'error'] as const).map((l) => (
          <button key={l} type="button" className={`tab ${level === l ? 'active' : ''}`} role="tab" aria-selected={level === l} onClick={() => setLevel(l)}>
            {l === 'all' ? `All ${entries.length}` : `${l} ${counts[l]}`}
          </button>
        ))}
        <span className="spacer" />
        <Button size="s" variant="ghost" onClick={() => onClear()} disabled={entries.length === 0}>
          Clear
        </Button>
      </div>
      {sources.length > 0 && (
        <div className="row wrap small" style={{ marginBottom: 8 }} aria-label="Entries per extension">
          {sources.map((s) => {
            const n = entries.filter((e) => e.source === s).length;
            return (
              <span key={s} className="row" data-testid={`log-source-${s}`}>
                <span className="badge badge-neutral">
                  {s} {n}
                </span>
                <Button size="s" variant="ghost" onClick={() => onClear(s)} aria-label={`Clear ${s}`}>
                  Clear {s}
                </Button>
              </span>
            );
          })}
        </div>
      )}
      {shown.length === 0 && <p className="muted">No log entries.</p>}
      {shown.map((e) => (
        <div key={e.id} className="log-line">
          <span className="muted">{formatInstant(Math.floor(e.at / 1000), 'utc', {}, { seconds: true }).slice(11)}</span>
          <span className={e.level === 'error' ? 'error' : e.level === 'warn' ? '' : 'muted'} style={e.level === 'warn' ? { color: 'var(--hue-warning)' } : undefined}>
            {e.level.toUpperCase()}
          </span>
          <span className="muted">{e.source}</span>
          <span>{e.message}</span>
          {e.data !== undefined && (
            <details>
              <summary>data</summary>
              <pre className="small">{JSON.stringify(e.data, null, 2)}</pre>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}
